/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies, no-unused-vars */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDebouncedCallback } from "@qlik/qlik/src/hooks/useDebouncedCallback";
import { getFilterTaxonomyTree, filterAndFlattenNonEmptyValues } from "@qlik/qlik/src/lib/util/filters";
import {
    DataSourceConstants,
    TaxonomyMapping as TaxonomyMappingRaw,
} from "@qlik/qlik/src/lib/constants/data-source-constants";
import CheckListFilterGroup from "@qlik/qlik/src/components/Common/CheckListFilterGroup";
import NoResults from "@qlik/qlik/src/components/Common/NoResults";
import DataSourceCard from "@qlik/qlik/src/components/Card/Single-Card-Data-Source";
import FilterChips from "@qlik/qlik/src/components/Common/FilterChips";
import { FilterContext } from "@qlik/qlik/src/context/FilterContext";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import DataSourceItems from "@qlik/qlik/src/graphql/queries/dataSources";
import useUrlQueryParamsState from "@qlik/qlik/src/hooks/useUrlQueryParamsState";
import Cta from "@qlik/qlik/src/components/Cta";
import { getCardBodyContent, getMicrocopyValueFromKeys } from "@qlik/qlik/src/lib/util/helpers";
import { useI18n } from "@qlik/utils/i18nContext";

/**
 * Displays a list of Data Sources with filtering and search functionality.
 *
 * @component
 * @param {Object} data - The data object containing configuration for the component.
 * @returns {JSX.Element} The DataSourceListing component.
 */
const DataSourceListing = ({ data }) => {
    const { additionalConfiguration, backgroundColor, cardItem, customId } = data || {};
    const { title, cta } = cardItem ?? {};
    const bodyContent = getCardBodyContent(cardItem, "mt-12.5");
    const { i18n } = useI18n();
    const dataSourceData = useMemo(() => DataSourceItems(i18n.curr.contentfulCode), [i18n.curr.contentfulCode]);

    const TaxonomyMapping = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(TaxonomyMappingRaw).map(([key, val]) => [
                    dataSourceData?.dataSourceTaxonomies?.nodes?.[0]?.terms?.find(
                        (el) => el.name === `Data Source ${key}`
                    )?.title,
                    val,
                ])
            ),
        [dataSourceData]
    );

    const [dataSources, setDataSources] = useState([]);
    // The list of currently visible Data sources depending on pagination.
    const [dataSourcesList, setDataSourcesList] = useState([]);
    const [selections, setSelections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [validFilters, setValidFilters] = useState({});

    const itemsPerPage = DataSourceConstants.DATA_SOURCE_NUMBER_PER_PAGE;
    const initialFilters = {
        page: 0,
        limit: itemsPerPage,
    };

    const [allParams, setAllParams] = useUrlQueryParamsState(initialFilters, TaxonomyMapping, itemsPerPage, true);
    const [hasMore, setHasMore] = useState(false);

    const shouldFilterByKeyword = allParams.keyword?.length;
    const shouldFilterByAnalytics = allParams.analytics?.length;
    const shouldFilterByApplication = allParams.application?.length;
    const shouldFilterByCloudComputing = allParams.cloudComputing?.length;
    const shouldFilterByDataIntegration = allParams.dataIntegration?.length;
    const shouldFilterByDeployment = allParams.deployment?.length;
    const shouldFilterByDeveloperRelated = allParams.developerRelated?.length;
    const shouldFilterByProductOrService = allParams.productOrService?.length;
    const shouldFilterByRepositories = allParams.repositories?.length;
    const shouldFilterBySecureAccess = allParams.secureAccess?.length;

    const [searchTerm, setSearchTerm] = useState("");
    const dataSourceTaxonomyTree = getFilterTaxonomyTree(dataSourceData.dataSourceTaxonomies, "Data Source");

    const dataList = useMemo(() => dataSourceData.dataSources.nodes, [dataSourceData.dataSources.nodes]);

    const [
        resultCountText,
        searchTermText,
        noResultsText,
        clearFiltersText,
        loadMoreText,
    ] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        DataSourceConstants.DATA_SOURCE_RESULTS_COUNT_TEXT,
        DataSourceConstants.DATA_SOURCE_TERM_SEARCH,
        DataSourceConstants.DATA_SOURCE_NO_RECORDS,
        DataSourceConstants.DATA_SOURCE_CLEAR_FILTERS,
        DataSourceConstants.DATA_SOURCE_LOAD_MORE,
    ]);

    const handleFiltering = useCallback(
        (items, filterBy) =>
            items
                .filter(
                    (dataSource) =>
                        (!filterBy.keyword ||
                            dataSource.title?.toLowerCase().includes(allParams.keyword?.toLowerCase())) &&
                        (!filterBy.analytics ||
                            (dataSource.analytics &&
                                dataSource.analytics.some((term) => allParams.analytics.includes(term.title)))) &&
                        (!filterBy.application ||
                            (dataSource.application &&
                                dataSource.application.some((term) => allParams.application.includes(term.title)))) &&
                        (!filterBy.cloudComputing ||
                            (dataSource.cloudComputing &&
                                dataSource.cloudComputing.some((term) =>
                                    allParams.cloudComputing.includes(term.title)
                                ))) &&
                        (!filterBy.dataIntegration ||
                            (dataSource.dataIntegration &&
                                dataSource.dataIntegration.some((term) =>
                                    allParams.dataIntegration.includes(term.title)
                                ))) &&
                        (!filterBy.deployment ||
                            (dataSource.deployment &&
                                dataSource.deployment.some((term) => allParams.deployment.includes(term.title)))) &&
                        (!filterBy.developerRelated ||
                            (dataSource.developerRelated &&
                                dataSource.developerRelated.some((term) =>
                                    allParams.developerRelated.includes(term.title)
                                ))) &&
                        (!filterBy.productOrService ||
                            (dataSource.productOrService &&
                                dataSource.productOrService.some((term) =>
                                    allParams.productOrService.includes(term.title)
                                ))) &&
                        (!filterBy.repositories ||
                            (dataSource.repositories &&
                                dataSource.repositories.some((term) => allParams.repositories.includes(term.title)))) &&
                        (!filterBy.secureAccess ||
                            (dataSource.secureAccess &&
                                dataSource.secureAccess.some((term) => allParams.secureAccess.includes(term.title))))
                ) // Ordering the data list without considering the case.
                .sort((a, b) => {
                    return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
                }),
        [allParams]
    );

    const checkIsDuplicate = (uniqueTitles, title) => {
        const isDuplicate = uniqueTitles.has(title);
        uniqueTitles.add(title);
        return !isDuplicate;
    };

    useEffect(() => {
        const uniqueTitles = new Set();
        const filteredDataSources = handleFiltering(dataList, {
            keyword: shouldFilterByKeyword,
            analytics: shouldFilterByAnalytics,
            application: shouldFilterByApplication,
            cloudComputing: shouldFilterByCloudComputing,
            dataIntegration: shouldFilterByDataIntegration,
            deployment: shouldFilterByDeployment,
            developerRelated: shouldFilterByDeveloperRelated,
            productOrService: shouldFilterByProductOrService,
            repositories: shouldFilterByRepositories,
            secureAccess: shouldFilterBySecureAccess,
        }).filter((dataSource) => {
            return checkIsDuplicate(uniqueTitles, dataSource.title);
        });
        setDataSources(filteredDataSources);
        setDataSourcesList([
            ...filteredDataSources.slice(
                0,
                (allParams.page + 1) * (allParams.page == 0 ? itemsPerPage : dataSources.length)
            ),
        ]);
        setIsLoading(false);
        let selectedFilters = allParams;
        selectedFilters = filterAndFlattenNonEmptyValues(selectedFilters, ["page", "limit"]);
        setSelections(selectedFilters);
    }, [
        dataList,
        handleFiltering,
        allParams,
        itemsPerPage,
        shouldFilterByKeyword,
        shouldFilterByAnalytics,
        shouldFilterByApplication,
        shouldFilterByCloudComputing,
        shouldFilterByDataIntegration,
        shouldFilterByDeployment,
        shouldFilterByDeveloperRelated,
        shouldFilterByProductOrService,
        shouldFilterByRepositories,
        shouldFilterBySecureAccess,
    ]);

    // Check if there are more events to show.
    useEffect(() => {
        const isMore = dataSourcesList.length < dataSources.length;
        setHasMore(isMore);
    }, [dataSourcesList, dataSources]);

    // Disable checkboxes based on what's available with the current filters.
    useEffect(() => {
        const filters = [
            "analytics",
            "application",
            "cloudComputing",
            "dataIntegration",
            "deployment",
            "developerRelated",
            "productOrService",
            "repositories",
            "secureAccess",
        ];

        const validFiltersObj = {};
        filters.forEach((currFilterType, i, arr) => {
            const filtered = handleFiltering(dataList, {
                keyword: shouldFilterByKeyword,
                analytics: shouldFilterByAnalytics && currFilterType !== "analytics",
                application: shouldFilterByApplication && currFilterType !== "application",
                cloudComputing: shouldFilterByCloudComputing && currFilterType !== "cloudComputing",
                dataIntegration: shouldFilterByDataIntegration && currFilterType !== "dataIntegration",
                deployment: shouldFilterByDeployment && currFilterType !== "deployment",
                developerRelated: shouldFilterByDeveloperRelated && currFilterType !== "developerRelated",
                productOrService: shouldFilterByProductOrService && currFilterType !== "productOrService",
                repositories: shouldFilterByRepositories && currFilterType !== "repositories",
                secureAccess: shouldFilterBySecureAccess && currFilterType !== "secureAccess",
            });
            filtered.forEach((item) => {
                item?.[currFilterType]?.forEach((term) => {
                    validFiltersObj[term.title] = true;
                });
            });
        });
        setValidFilters(validFiltersObj);
    }, [
        dataList,
        handleFiltering,
        shouldFilterByKeyword,
        shouldFilterByAnalytics,
        shouldFilterByApplication,
        shouldFilterByCloudComputing,
        shouldFilterByDataIntegration,
        shouldFilterByDeployment,
        shouldFilterByDeveloperRelated,
        shouldFilterByProductOrService,
        shouldFilterByRepositories,
        shouldFilterBySecureAccess,
    ]);
    // create debounced callback - API call for search query update
    const debouncedSearchInputChangeHandler = useDebouncedCallback((value) => {
        // only fetch for 3 or more letter queries
        if (value.length < 3) {
            setAllParams((prevParams) => {
                const { keyword: removedKey, ...newParams } = prevParams;
                return newParams;
            });
        } else {
            setAllParams({ ...allParams, keyword: value });
        }
    });

    // Handle "Show All" button click
    const handleLoadMore = () => {
        if (hasMore) {
            const currentLength = dataSourcesList.length;
            const isMore = currentLength < dataSources.length;
            const nextResults = isMore ? dataSources.slice(currentLength, currentLength + dataSources.length) : [];
            // Create a Set with titles from the current dataSourcesList
            const uniqueTitles = new Set(dataSourcesList.map((item) => item.title));

            // Filter out duplicates from nextResults based on the title
            const uniqueNextResults = nextResults.filter((dataSource) => {
                return checkIsDuplicate(uniqueTitles, dataSource.title);
            });

            setDataSourcesList([...dataSourcesList, ...uniqueNextResults]);
            setAllParams({ ...allParams, page: allParams.page + 1 });
        }
    };

    const resetFilters = () => {
        setAllParams({ page: 0 });
        setSelections([]);
        setSearchTerm("");
    };

    const onChangeSearch = (e) => {
        e.preventDefault();
        const keyword = e?.target?.value;
        debouncedSearchInputChangeHandler(keyword);
        setSearchTerm(keyword);
    };

    const removeCurrentSelection = (filterName, label) => {
        const currentSelection = selections;
        if (filterName === "keyword") {
            setAllParams((prevParams) => {
                const { keyword: removedKey, ...newParams } = prevParams;
                return newParams;
            });
            setSearchTerm("");
        } else {
            const updatedFilterList = allParams[filterName]?.filter((filter) => filter !== label);
            setAllParams((prevParams) => ({ ...prevParams, [filterName]: updatedFilterList }));
        }
        setSelections(currentSelection?.slice(label, 1));
    };

    const {
        sectionColorMapping: { bgColor },
    } = getColorClassName(backgroundColor);
    const spacingClassName = getSpacingClassName(additionalConfiguration, "py-15");

    return (
        <section className={`${bgColor} ${spacingClassName}`} id={customId}>
            <div className="container">
                <div className="row">
                    <div className="md:col-4 xl:col-3">
                        <div>
                            <p className="mb-7.5 title-5">{`${dataSources.length} ${resultCountText}`}</p>
                        </div>
                        <div className="pb-7.5 md:pb-0">
                            <label htmlFor="search" className="sr-only">
                                Search
                            </label>
                            <input
                                id="search"
                                type="text"
                                placeholder={searchTermText}
                                onChange={onChangeSearch}
                                value={searchTerm}
                                className="body w-full h-15 py-4 pl-15 pr-5 mb-7.75 border border-stone bg-filterSearch bg-no-repeat bg-filter-search-icon rounded placeholder:text-gray"
                            />
                            {/* Render filters for each top level category */}
                            <FilterContext.Provider value={validFilters}>
                                {dataSourceTaxonomyTree?.map((singleTaxonomy, i) => {
                                    return (
                                        singleTaxonomy.subTerms.length && (
                                            <CheckListFilterGroup
                                                key={singleTaxonomy.id}
                                                filterName={TaxonomyMapping[singleTaxonomy.title]}
                                                parentCategory={singleTaxonomy.title}
                                                categoryData={singleTaxonomy.subTerms}
                                                allParams={allParams}
                                                setAllParams={setAllParams}
                                                index={i}
                                            />
                                        )
                                    );
                                })}
                            </FilterContext.Provider>
                            {selections.length ? (
                                <button
                                    className="hidden md:inline-block mt-8.75 text-xs lg:text-base text-green tracking-wide4 cursor-pointer uppercase"
                                    onClick={resetFilters}
                                >
                                    <span className="inline-block w-3 h-3 mr-2.5 bg-closeIconGreenMd bg-no-repeat" />
                                    {clearFiltersText}
                                </button>
                            ) : null}
                        </div>
                    </div>
                    <div className="md:col-8 xl:col-9 xl:pl-11.25">
                        {selections.length ? (
                            <div className="flex flex-wrap gap-2.5 pb-7.5 lg:pb-15 pt-5 sm:pt-0">
                                <FilterChips
                                    filters={allParams}
                                    onClose={removeCurrentSelection}
                                    onResetFilters={resetFilters}
                                    resetFiltersLabel={clearFiltersText}
                                />
                            </div>
                        ) : null}
                        {!isLoading && !dataSourcesList.length && (
                            <div className="px-5 lg:px-10 col-span-2">
                                <NoResults text={noResultsText} />
                            </div>
                        )}
                        <div>
                            <div className="grid gap-3.75 pb-10 lg:pb-20 md:grid-cols-2 xl:grid-cols-3 lg:gap-7.5">
                                {dataSourcesList?.map((singleItem) => (
                                    <DataSourceCard card={singleItem} key={singleItem.id} />
                                ))}
                            </div>
                        </div>
                        <div>
                            {hasMore && (
                                <div className="flex justify-center">
                                    <button
                                        type="button"
                                        className="btn-secondary-small min-w-[157px]"
                                        aria-label={loadMoreText}
                                        onClick={handleLoadMore}
                                    >
                                        {loadMoreText}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div>
                            {title && <h2 className="title-2">{title}</h2>}
                            {bodyContent && bodyContent}
                            {/* Rendering the CTA if it exists */}
                            {cta?.length > 0 && (
                                <div className="mt-2.5 flex flex-col lg:flex-row lg:flex-wrap items-center gap-x-5 lg:mt-7.5 lg:justify-start">
                                    {cta?.map((button) => (
                                        <Cta key={button.id} data={button} className="mt-5" />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DataSourceListing;
